'use client';

import cn from '@/utils/cn';
import { XCloseIcon } from '@tajdid/tajdid-ui/icons';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

type ChildrenProps = {
  children?: React.ReactNode;
  className?: string;
};

const dialogMainVariants = cva(
  'p-5 fixed -translate-x-1/2 -translate-y-1/2 bg-white rounded-md top-1/2 left-1/2 max-h-85vh',
  {
    variants: {
      size: {
        sm: 'w-[400px]',
        md: 'w-[900px]',
        lg: 'w-[1200]',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

export type DialogMainProps = VariantProps<typeof dialogMainVariants> &
  ChildrenProps &
  DialogPrimitive.DialogProps;

const Dialog = ({
  children,
  open,
  onOpenChange,
  defaultOpen,
  ...props
}: DialogMainProps) => (
  <DialogPrimitive.Root
    open={open}
    defaultOpen={defaultOpen}
    onOpenChange={onOpenChange}
  >
    {React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { ...props });
      }
      return child;
    })}
  </DialogPrimitive.Root>
);

const DialogMain = ({ children, className, size }: DialogMainProps) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay
      className="fixed inset-0 z-40"
      style={{
        background: 'rgba(52, 64, 84, 0.70)',
        backdropFilter: 'blur(8px)',
      }}
    />
    <DialogPrimitive.Content
      className={cn('z-50', dialogMainVariants({ size, className }))}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
);

const DialogTitle = ({ children, className }: ChildrenProps) => (
  <DialogPrimitive.Title
    className={cn('text-lg font-semibold text-gray-900', className)}
  >
    {children}
  </DialogPrimitive.Title>
);

const DialogSubtitle = ({ children, className }: ChildrenProps) => (
  <DialogPrimitive.Title
    className={cn('mb-2 text-sm text-gray-600', className)}
  >
    {children}
  </DialogPrimitive.Title>
);

const DialogContent = ({ children, className }: ChildrenProps) => (
  <div className={cn('mt-4', className)}>{children}</div>
);

const DialogDivider = ({ className }: { className?: string }) => (
  <div className={cn('border-b-1 border-gray-200', className)} />
);

const DialogAction = ({ children, className }: ChildrenProps) => (
  <div className={cn('flex justify-end mt-9 gap-x-3', className)}>
    {children}
  </div>
);

const dialogIconVariants = cva(
  'inline-flex items-center justify-center w-12 h-12 mb-4 border-8 rounded-full',
  {
    variants: {
      color: {
        error: 'text-error-600 border-error-50 bg-error-100',
        success: 'text-primary-600 border-primary-50 bg-primary-100',
      },
    },
  },
);

const DialogIcon = ({
  children,
  className,
  color,
}: ChildrenProps & VariantProps<typeof dialogIconVariants>) => (
  <div className={cn(dialogIconVariants({ color, className }))}>{children}</div>
);

const DialogTrigger = ({ children }: ChildrenProps) => (
  <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>
);

const DialogCloseButton = () => (
  <DialogPrimitive.Close
    aria-label="Close"
    className="absolute top-3 right-3 inline-flex items-center justify-center rounded-sm p-[10px] text-gray-700 hover:bg-gray-50 focus-visible:outline-none"
  >
    <XCloseIcon className="text-gray-500" width="20" height="20" />
  </DialogPrimitive.Close>
);

Dialog.Root = Dialog;
Dialog.Trigger = DialogTrigger;
Dialog.Main = DialogMain;
Dialog.Content = DialogContent;
Dialog.Title = DialogTitle;
Dialog.Divider = DialogDivider;
Dialog.Action = DialogAction;
Dialog.Close = DialogPrimitive.Close;
Dialog.CloseButton = DialogCloseButton;
Dialog.Subtitle = DialogSubtitle;
Dialog.Icon = DialogIcon;

export { Dialog };
